import React, { useEffect, useState } from 'react';
import api from '../api/axios';

// آیکون‌های SVG
const Icons = {
  ChevronDown: () => (
    <svg
      className="w-5 h-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2">
      <path d="M6 9l6 6 6-6" />
    </svg>
  ),
  ChevronUp: () => (
    <svg
      className="w-5 h-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2">
      <path d="M18 15l-6-6-6 6" />
    </svg>
  ),
  Refresh: () => (
    <svg
      className="w-4 h-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2">
      <path d="M23 4v6h-6M1 20v-6h6" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  ),
  Check: () => (
    <svg
      className="w-5 h-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  ),
  Alert: () => (
    <svg
      className="w-5 h-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  ),
  Search: () => (
    <svg
      className="w-5 h-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2">
      <circle cx="11" cy="11" r="8"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
  ),
  UploadCloud: () => (
    <svg
      className="w-5 h-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2">
      <polyline points="16 16 12 12 8 16"></polyline>
      <line x1="12" y1="12" x2="12" y2="21"></line>
      <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"></path>
      <polyline points="16 16 12 12 8 16"></polyline>
    </svg>
  ),
};

const MigrationDashboard = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [groupedVideos, setGroupedVideos] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});

  const [fileStatus, setFileStatus] = useState({}); // وضعیت آپلود: idle, loading, success, error
  const [linkHealth, setLinkHealth] = useState({}); // وضعیت سلامت لینک: checking, ok, dead
  const [progress, setProgress] = useState({}); // درصد پیشرفت

  // دامین R2 (با https و پسوند صحیح)
  const R2_DOMAIN_KEY = 'azishafiei';

  useEffect(() => {
    fetchVideos();
  }, []);

  // گروه‌بندی ویدیوها
  useEffect(() => {
    if (videos.length > 0) {
      const grouped = videos.reduce((acc, video) => {
        const key = video.product || 'سایر (بدون دسته‌بندی)';
        if (!acc[key]) acc[key] = [];
        acc[key].push(video);
        return acc;
      }, {});
      setGroupedVideos(grouped);
      // باز کردن اولین گروه به صورت پیش‌فرض
      const firstKey = Object.keys(grouped)[0];
      setExpandedGroups((prev) => ({ ...prev, [firstKey]: true }));
    }
  }, [videos]);

  const fetchVideos = async () => {
    setLoading(true);
    try {
      const res = await api.get('/sessionvideo/all');
      const sorted = res.data.sort(
        (a, b) => (a.session || 0) - (b.session || 0)
      );
      setVideos(sorted);
    } catch (err) {
      console.error(err);
      alert('خطا در دریافت لیست');
    } finally {
      setLoading(false);
    }
  };

  const toggleGroup = (groupName) => {
    setExpandedGroups((prev) => ({ ...prev, [groupName]: !prev[groupName] }));
  };

  // شبیه‌ساز پروگرس بار
  const simulateProgress = (id) => {
    setProgress((prev) => ({ ...prev, [id]: 0 }));
    const interval = setInterval(() => {
      setProgress((prev) => {
        const current = prev[id] || 0;
        if (current >= 90) return current;
        const diff = Math.random() * (current < 50 ? 10 : 2);
        return Math.min(current + diff, 90);
      });
    }, 500);
    return interval;
  };

  // تابع انتقال (یا آپلود مجدد)
  const handleMigrate = async (video, force = false) => {
    if (
      force &&
      !window.confirm(`آیا از آپلود مجدد "${video.name}" اطمینان دارید؟`)
    )
      return;

    setFileStatus((prev) => ({ ...prev, [video._id]: 'loading' }));
    // اگر داریم دوباره آپلود می‌کنیم، وضعیت سلامت قبلی را پاک کن
    if (force) setLinkHealth((prev) => ({ ...prev, [video._id]: null }));

    const progressInterval = simulateProgress(video._id);

    try {
      const res = await api.post(
        '/migration/migrate-video',
        { videoId: video._id, force: force },
        { timeout: 0 }
      );

      clearInterval(progressInterval);
      setProgress((prev) => ({ ...prev, [video._id]: 100 }));
      setFileStatus((prev) => ({ ...prev, [video._id]: 'success' }));
      // فرض بر این است که بعد از آپلود موفق، لینک سالم است
      setLinkHealth((prev) => ({ ...prev, [video._id]: 'ok' }));

      if (res.data.success) {
        setVideos((prev) =>
          prev.map((v) =>
            v._id === video._id ? { ...v, link: res.data.newLink } : v
          )
        );
      }
    } catch (err) {
      clearInterval(progressInterval);
      setProgress((prev) => ({ ...prev, [video._id]: 0 }));
      setFileStatus((prev) => ({ ...prev, [video._id]: 'error' }));
      alert(`خطا: ${err.response?.data?.message || err.message}`);
    }
  };

  // --- اصلاح شده: بررسی سلامت فایل توسط سرور ---
  const verifyAllLinks = async () => {
    // فیلتر کردن فایل‌هایی که لینک R2 دارند
    const r2Videos = videos.filter((v) => isMigrated(v.link));

    if (r2Videos.length === 0) {
      alert('هیچ فایلی برای بررسی وجود ندارد.');
      return;
    }

    if (
      !window.confirm(
        `تعداد ${r2Videos.length} فایل بررسی می‌شود. ادامه می‌دهید؟`
      )
    )
      return;

    // ست کردن وضعیت اولیه همه به 'checking'
    const newStatus = {};
    r2Videos.forEach((v) => (newStatus[v._id] = 'checking'));
    setLinkHealth((prev) => ({ ...prev, ...newStatus }));

    // بررسی دانه‌دانه (برای جلوگیری از فشار به سرور)
    for (const video of r2Videos) {
      try {
        // درخواست به بک‌‌اند خودمان
        await api.post('/migration/check-link', { url: video.link });

        // اگر ارور نداد یعنی OK است
        setLinkHealth((prev) => ({ ...prev, [video._id]: 'ok' }));
      } catch (error) {
        console.error('Check failed for:', video.name);
        setLinkHealth((prev) => ({ ...prev, [video._id]: 'dead' }));
      }
    }
    alert('بررسی تمام شد.');
  };

  const isMigrated = (link) => {
    return link && link.includes(R2_DOMAIN_KEY) && !link.startsWith('ttps');
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 font-sans text-right" dir="rtl">
      <div className="max-w-6xl mx-auto">
        {/* Header Control Panel */}
        <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 mb-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              پنل مهاجرت ویدیوها (R2)
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              مدیریت انتقال فایل‌ها از Google Drive به Cloudflare R2
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={verifyAllLinks}
              className="flex items-center gap-2 bg-purple-100 text-purple-700 hover:bg-purple-200 px-4 py-2 rounded-lg transition text-sm font-medium">
              <Icons.Search />
              <span>بررسی سلامت فایل‌ها</span>
            </button>

            <button
              onClick={fetchVideos}
              className="flex items-center gap-2 bg-gray-100 text-gray-700 hover:bg-gray-200 px-4 py-2 rounded-lg transition text-sm font-medium">
              <Icons.Refresh />
              <span>بروزرسانی لیست</span>
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20 text-gray-400">
            در حال بارگذاری لیست...
          </div>
        ) : (
          <div className="space-y-4">
            {Object.keys(groupedVideos).map((productName) => {
              const isOpen = expandedGroups[productName];
              const groupItems = groupedVideos[productName];

              // محاسبه آمار هر گروه
              const total = groupItems.length;
              const migrated = groupItems.filter((v) =>
                isMigrated(v.link)
              ).length;
              const deadLinks = groupItems.filter(
                (v) => linkHealth[v._id] === 'dead'
              ).length;

              return (
                <div
                  key={productName}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  {/* Group Header */}
                  <button
                    onClick={() => toggleGroup(productName)}
                    className="w-full flex justify-between items-center p-4 bg-gray-50 hover:bg-gray-100 transition text-right">
                    <div className="flex items-center gap-3">
                      {isOpen ? <Icons.ChevronUp /> : <Icons.ChevronDown />}
                      <span className="font-bold text-gray-800">
                        {productName}
                      </span>
                      <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-full font-mono">
                        {total}
                      </span>
                    </div>

                    <div className="flex gap-3 text-sm">
                      {deadLinks > 0 && (
                        <span className="text-red-600 font-bold bg-red-50 px-2 py-1 rounded flex items-center gap-1">
                          <Icons.Alert /> {deadLinks} فایل خراب
                        </span>
                      )}
                      <span
                        className={
                          migrated === total
                            ? 'text-green-600 font-medium'
                            : 'text-gray-500'
                        }>
                        {migrated} / {total} منتقل شده
                      </span>
                    </div>
                  </button>

                  {/* Group Content */}
                  {isOpen && (
                    <div className="divide-y divide-gray-100">
                      {groupItems.map((video) => {
                        const migrated = isMigrated(video.link);
                        const status = fileStatus[video._id];
                        const prog = progress[video._id] || 0;
                        const health = linkHealth[video._id]; // checking, ok, dead

                        // رنگ پس‌زمینه بر اساس وضعیت
                        let rowClass = 'hover:bg-gray-50';
                        if (health === 'dead')
                          rowClass = 'bg-red-50 hover:bg-red-100';
                        else if (status === 'success') rowClass = 'bg-green-50';

                        return (
                          <div
                            key={video._id}
                            className={`p-4 transition relative ${rowClass}`}>
                            {/* نوار پیشرفت */}
                            {status === 'loading' && (
                              <div
                                className="absolute bottom-0 right-0 h-1 bg-blue-500 transition-all duration-300"
                                style={{ width: `${prog}%` }}
                              />
                            )}

                            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                              {/* بخش اطلاعات */}
                              <div className="flex-1 w-full min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="bg-gray-200 text-gray-600 text-[10px] px-1.5 py-0.5 rounded font-mono">
                                    S-{video.session}
                                  </span>
                                  <h3 className="font-semibold text-gray-800 text-sm truncate">
                                    {video.name}
                                  </h3>

                                  {/* نشانگر وضعیت سلامت فایل */}
                                  {health === 'checking' && (
                                    <span className="text-xs text-blue-500 animate-pulse">
                                      در حال بررسی...
                                    </span>
                                  )}
                                  {health === 'dead' && (
                                    <span className="text-xs text-red-600 font-bold flex items-center gap-1">
                                      <Icons.Alert /> فایل یافت نشد (404)
                                    </span>
                                  )}
                                  {health === 'ok' && (
                                    <span className="text-xs text-green-600 font-bold flex items-center gap-1">
                                      <Icons.Check /> سالم
                                    </span>
                                  )}
                                </div>

                                <div
                                  className="text-[11px] text-gray-400 font-mono truncate ltr text-left"
                                  title={video.link}>
                                  {video.link}
                                </div>
                              </div>

                              {/* بخش عملیات */}
                              <div className="flex items-center gap-3">
                                {migrated ? (
                                  <>
                                    {/* اگر فایل منتقل شده، نمایش وضعیت */}
                                    <div
                                      className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-medium ${
                                        health === 'dead'
                                          ? 'bg-red-100 text-red-700 border-red-200'
                                          : 'bg-green-50 text-green-700 border-green-200'
                                      }`}>
                                      {health === 'dead'
                                        ? 'فایل ناقص'
                                        : 'روی سرور R2'}
                                    </div>

                                    {/* دکمه آپلود مجدد (همیشه فعال برای موارد سبز) */}
                                    <button
                                      onClick={() => handleMigrate(video, true)} // Force Upload
                                      disabled={status === 'loading'}
                                      title="آپلود مجدد (تعمیر)"
                                      className="p-2 text-gray-500 bg-white border border-gray-200 hover:text-blue-600 hover:border-blue-300 rounded-lg transition shadow-sm">
                                      <Icons.Refresh />
                                    </button>
                                  </>
                                ) : (
                                  // دکمه انتقال اولیه
                                  <button
                                    onClick={() => handleMigrate(video, false)}
                                    disabled={status === 'loading'}
                                    className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm transition shadow-md disabled:bg-indigo-300 disabled:cursor-wait">
                                    <Icons.UploadCloud />
                                    <span>
                                      {status === 'loading'
                                        ? `${Math.round(prog)}%`
                                        : 'انتقال'}
                                    </span>
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MigrationDashboard;
